import { CustomTheme, createTheme } from "@mui/material/styles";
import { grey } from "@mui/material/colors";
import React from "react";
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from "react-router-dom";
import {
  touchRippleClasses,
  autocompleteClasses,
  chipClasses,
  fabClasses,
  listItemClasses,
  listItemButtonClasses,
} from "@mui/material";

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, "to"> & { href: RouterLinkProps["to"] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (Material UI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});
LinkBehavior.displayName = "LinkBehavior";

const { palette } = createTheme({
  palette: {
    text: {
      primary: "#081e43",
      secondary: "#081e4399",
      disabled: "#081e4361",
    },
    primary: {
      main: "#081E43",
      dark: "#05152E",
      light: "#394B68",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#aaa1fd",
      dark: "#745de5",
      light: "#cdc6fe",
      contrastText: "#081E43",
    },
    action: {
      active: "#aaa1fd",
      hover: "rgba(116, 93, 229, 0.04)",
      selected: "rgba(116, 93, 229, 0.12)",
      focus: "rgba(116, 93, 229, 0.16)",
      disabledBackground: "#0000001e",
      disabled: "rgba(0, 0, 0, 0.23)",
    },
    error: {
      main: "#FB6A76",
      dark: "#ec0031",
      light: "#FFF0F1",
      contrastText: "#081E43",
    },
    warning: {
      main: "#FFBD5B",
      dark: "#C55011",
      light: "#FFF8EF",
      contrastText: "#081E43",
    },
    info: {
      main: "#65ABE8",
      dark: "#226bc0",
      light: "#8FC2EF",
      contrastText: "#081E43",
    },
    success: {
      main: "#3EC490",
      dark: "#007840",
      light: "#ECF9F4",
      contrastText: "#081e43",
    },
    background: {
      default: "#ffffff",
      // all paper elevations are pure white
      paper: "#ffffff",
    },
    customBackground: {
      main: "#FAFAFF",
    },
    customAction: {
      primaryHover: {
        main: "#081e430a",
      },
    },
    borderColor: {
      main: "#CFCDC9",
      light: grey[300],
    },

    skeleton: {
      main: grey[300],
      levelOne: grey[200],
      levelTwo: grey[100],
      levelThree: grey[100],
      levelFour: grey[50],
    },
    divider: "#081e431e",
  },
});

const theme: CustomTheme = createTheme({
  palette,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  typography: {
    fontFamily: "Poppins",
    subtitle1: {
      fontWeight: 500,
    },
  },
  components: {
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      },
      styleOverrides: {
        root: {
          ":focus-visible": {
            outlineColor: palette.secondary.main,
          },
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: "black",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // white text when hovered
        contained: {
          ":hover": { color: "white" },
        },
      },
      // darken text of outlined and text variants
      // (per-color class keys like outlinedError were removed in MUI v9)
      variants: (["outlined", "text"] as const).flatMap((variant) =>
        (["error", "info", "warning", "success", "secondary"] as const).map(
          (color) => ({
            props: { variant, color },
            style: { color: palette[color].dark },
          }),
        ),
      ),
    },
    MuiFormHelperText: {
      styleOverrides: {
        // darken error helper text
        root: {
          "&.Mui-error": {
            color: palette.error.dark,
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          ":hover": { color: "white" },
          [`:is(.${fabClasses.colorInherit}, .MuiFab-default):hover`]: {
            color: "inherit",
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.secondary.main,
          color: palette.primary.dark,
        },
      },
    },
    MuiBadge: {
      defaultProps: {
        color: "error",
      },
      styleOverrides: {
        badge: {
          ":not([class*='MuiBadge-color'])": {
            backgroundColor: "#CFCDC9",
            color: palette.text.primary,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorSuccess: {
          color: palette.success.dark,
          borderColor: palette.success.dark,
        },

        // white text when hovered
        filled: {
          [`&.${chipClasses.clickable}:hover`]: { color: "white" },
          [`&.${chipClasses.colorDefault}:hover`]: { color: "unset" },
        },
        root: {
          // all avatar colour variants use text.primary besides primary
          [`.${chipClasses.avatar}`]: {
            color: palette.text.primary,
          },
          [`&.${chipClasses.colorPrimary} > .${chipClasses.avatar}`]: {
            color: palette.primary.contrastText,
            backgroundColor: palette.primary.light,
          },
          // avatar background colours are color.light
          [`&.${chipClasses.colorSecondary} > .${chipClasses.avatar}`]: {
            backgroundColor: palette.secondary.light,
          },
          [`&.${chipClasses.colorInfo} > .${chipClasses.avatar}`]: {
            backgroundColor: palette.info.light,
          },
          [`&.${chipClasses.colorWarning} > .${chipClasses.avatar}`]: {
            backgroundColor: palette.warning.light,
          },
          [`&.${chipClasses.colorError} > .${chipClasses.avatar}`]: {
            backgroundColor: palette.error.light,
          },
          [`&.${chipClasses.colorDefault} > .${chipClasses.avatar}`]: {
            // except default
            backgroundColor: palette.secondary.main,
          },
          // arbitrary delete icon colour :(
          [`&.${chipClasses.colorDefault} > .${chipClasses.deleteIcon}`]: {
            color: "#430098",
            opacity: 0.26,
          },
          // outlined variant uses dark version of respective colour
          [`&.${chipClasses.outlined}.${chipClasses.colorPrimary}`]: {
            color: palette.primary.dark,
          },
          [`&.${chipClasses.outlined}.${chipClasses.colorSecondary}`]: {
            color: palette.secondary.dark,
          },
          [`&.${chipClasses.outlined}.${chipClasses.colorInfo}`]: {
            color: palette.info.dark,
          },
          [`&.${chipClasses.outlined}.${chipClasses.colorWarning}`]: {
            color: palette.warning.dark,
          },
          [`&.${chipClasses.outlined}.${chipClasses.colorError}`]: {
            color: palette.error.dark,
          },
          [`&.${chipClasses.outlined}.${chipClasses.colorDefault}`]: {
            color: palette.primary.dark,
            borderColor: palette.action.active,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          // left line and background for selected
          [`.${listItemButtonClasses.root}.Mui-selected`]: {
            backgroundColor: palette.secondary.light,
            borderLeft: `4px solid ${palette.primary.main}`,
          },
          // border radius for all top-level list items
          [`.${listItemClasses.root}, .${listItemButtonClasses.root}`]: {
            borderRadius: "4px",
          },
          [`.${listItemClasses.gutters}, .${listItemButtonClasses.gutters}`]: {
            paddingLeft: "12px",
            paddingRight: 0,
          },
          // get rid of jank overlay radius onClick
          [`.Mui-selected>.${touchRippleClasses.root}`]: {
            borderRadius: "0px 4px 4px 0px",
          },
        },
      },
    },
    // prefer secondary colour for progress
    MuiLinearProgress: {
      defaultProps: {
        color: "secondary",
      },
    },
    MuiCircularProgress: {
      defaultProps: {
        color: "secondary",
      },
    },
    // snackbar action text should be secondary.light
    MuiSnackbarContent: {
      styleOverrides: {
        action: {
          color: palette.secondary.light,
        },
      },
    },
    MuiStepLabel: {
      // step label uses dark version of color
      styleOverrides: {
        label: {
          "&.Mui-error": {
            color: palette.error.dark,
          },
        },
      },
    },
    MuiStepIcon: {
      // step icons must have 24px min width
      styleOverrides: {
        root: {
          minWidth: "24px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        // tab text is darker when secondary
        textColorSecondary: {
          "&.Mui-selected": {
            color: palette.secondary.dark,
          },
          "&.Mui-selected svg": {
            color: palette.secondary.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // tab indicator is darker when secondary
        indicator: ({ ownerState: { indicatorColor } }) => ({
          backgroundColor:
            indicatorColor === "secondary"
              ? palette.secondary.dark
              : palette.primary.main,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        // default colour is primary
        root: {
          ":not([class*='MuiIconButton-color'])": {
            color: palette.primary.main,
          },
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          [`&.${autocompleteClasses.popper}>.${autocompleteClasses.paper}`]: {
            ".Mui-selected.Mui-focused, .Mui-selected": {
              backgroundColor: palette.action.focus,
              ":hover": {
                backgroundColor: palette.action.focus,
              },
            },
          },
        },
      },
    },
  },
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
  },
});

export type PaletteColour =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "warning"
  | "info"
  | "success"
  | "text"
  | "skeleton";

export default theme;
